import { BreadcrumbProps } from '@blueprintjs/core'
import { computed, makeAutoObservable } from 'mobx'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import React, { Fragment, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ActionBar } from '../components/ActionBar'
import { TargetingTypeSmall } from '../components/badge/TargetingTypeSmall'
import { Actions } from '../components/details/UpdateActions'
import { Layout } from '../components/Layout'
import { LogsTable } from '../components/logs/LogsTable'
import { TableStore } from '../components/table2/table_store'
import { TargetACOS } from '../components/TargetACOS'
import { TabbedCards } from '../components/ui/TabbedCards'
import { EventParentTypes } from '../config'
import { useOnProfileChange } from '../hooks/useOnProfileChange'
import { useSavedFiltersStore } from '../hooks/useSavedFiltersStore'
import { useScreenshotMode } from '../hooks/useScreenshotMode'
import { useUser } from '../hooks/useUser'
import { useTableHooksFromStore } from '../packages/blueprint-components/src/useTableHooks'
import { userStore } from '../stores/user_store'
import { axiosGet, axiosPatch } from '../util/api'
import { adManagerPerformanceGet } from '../util/adManagerPerformanceApi'
import { OverviewCharts } from './dashboard-overview-charts'

interface SubStores {
    logs: TableStore<any>
}

class DetailStore {
    activeTab: string = ''
    subStores: SubStores

    constructor(subStores: SubStores) {
        this.subStores = subStores
        makeAutoObservable(this)
    }

    setActiveTab(tab: string) {
        this.activeTab = tab
    }

    get dates(): { from: Date; to: Date } {
        switch (this.activeTab) {
            case 'logs':
                return {
                    from: this.subStores.logs.params.from,
                    to: this.subStores.logs.params.to,
                }
            default:
                console.warn('unknown tab', this.activeTab)
                return {
                    // 30 days ago
                    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                    to: new Date(),
                }
        }
    }

    @computed({ equals: (a: Date, b: Date) => a.getTime() / 86400 === b.getTime() / 86400 })
    get from() {
        return this.dates.from
    }

    @computed({ equals: (a: Date, b: Date) => a.getTime() / 86400 === b.getTime() / 86400 })
    get to() {
        return this.dates.to
    }
}

/**
 * Target Detail Page (v5 - Core/PostgreSQL)
 *
 * Uses v5 API endpoints backed by Core/PostgreSQL for target data.
 * This is the only target detail implementation.
 */
export const TargetDetailV5 = observer(() => {
    const { target_id } = useParams()
    const [target, setTarget] = useState(null)
    const { profile, searchParams } = useUser()
    const nameClassName = classNames({ blur: useScreenshotMode() })
    const [logsStore] = useState(new TableStore<any>(userStore, `logs_for_target_v5_${target_id}`))
    const [detailStore] = useState(new DetailStore({ logs: logsStore }))
    const savedFiltersStore = useSavedFiltersStore()

    // Load target metadata from the v5 Core API.
    const load = (params = null) => {
        if (!profile) {
            return
        }

        adManagerPerformanceGet(`api/v5/targets/${target_id}`, params ?? {}, true).then((res) => {
            setTarget(res.data)
        })
    }

    // Updates still go through Laravel API
    const update = (payload) => axiosPatch(`targets/${target_id}`, payload)

    // Logs still use Laravel API
    const logsLoad = (params) => {
        if (!profile) {
            return Promise.resolve()
        }

        const p = {
            ...params,
            filter: {
                ...params.filter,
                target_type_id: EventParentTypes.TARGETS,
                target_id: target_id, // MER-2802: keep the URL param as a string; parseInt truncates
            },
        }

        return axiosGet('logs', p)
    }
    const logsTableHooks = useTableHooksFromStore(logsLoad, {}, false, logsStore)

    useOnProfileChange(() => {
        load()
    })

    useEffect(() => {
        load()
    }, [searchParams])

    const actions = (
        <Fragment>
            <ActionBar className="flex items-center justify-between pl-2 pr-4">
                <div className="flex items-center">
                    {target && (
                        <Fragment>
                            <TargetingTypeSmall
                                className="mr-2"
                                targeting_type={target.expression_type}
                                state={target.state}
                            />

                            <div className="flex justify-center mr-2">
                                <span className="mr-1">Target ACoS</span>
                                <TargetACOS
                                    target="targets"
                                    product={target}
                                    mutateItems={(cb) => setTarget((prev) => cb([prev])[0])}
                                />
                            </div>
                        </Fragment>
                    )}
                </div>
                <Actions name={'Target'} reloadParent={load} data={target} update={update} />
            </ActionBar>
        </Fragment>
    )

    const breadcrumbs: BreadcrumbProps[] = [
        { text: 'Manage' },
        {
            href: '/campaigns/' + target?.campaign_id,
            text: 'Campaign: ' + target?.campaign_name,
            className: nameClassName,
        },
        {
            href: '/ad-groups/' + target?.ad_group_id,
            text: 'Ad Group: ' + target?.ad_group_name,
            className: nameClassName,
        },
        { text: 'Target:' },
    ]

    return (
        <Layout
            primary={<span className={nameClassName}>{target?.targeting_expression}</span>}
            breadcrumbs={breadcrumbs}
            actions={actions}
        >
            <OverviewCharts
                params={{
                    type: 'targets',
                    target_id,
                    from: detailStore.from,
                    to: detailStore.to,
                    previous: true,
                }}
            />

            <div className="mb-4" />
            <TabbedCards tabs={['Logs']} onChange={(tab) => detailStore.setActiveTab(tab)}>
                <LogsTable
                    store={logsStore}
                    savedFiltersStore={savedFiltersStore}
                    tableHooks={logsTableHooks}
                    nested={true}
                />
            </TabbedCards>
        </Layout>
    )
})
